import React, { useCallback, useMemo, useState } from "react";
import Wrap from "../components/Wrap";
import Card from "../components/ui/Card";
import { useParams, useSearchParams } from "react-router-dom";
import useAuthStore from "../services/AuthService";
import Header from "../components/layouts/Header";
import { Toaster } from "../components/ui/toast/toaster";
import ActivityTab from "../features/activity/ActivityTab";
import { localizationClient } from "../api/v2/localizationClient";
import { parseApiError } from "../api/http/errors";
import {
  RepositoryGroup,
  WorkspaceConfig,
  WorkspaceStateResponse,
  WorkspaceSummary,
} from "../api/v2/types";
import BindingsTab from "../features/bindings/BindingsTab";
import GroupsManager from "../features/repositoryGroups/GroupsManager";
import { useToast } from "../components/ui/toast/use-toast";
import { ToastAction } from "../components/ui/toast/toast";
import EditorTab from "../features/editorV2/EditorTab";
import { useWorkspaceEditorParams } from "../features/workspaceContext/useWorkspaceEditorParams";
import InlineHint from "../features/shared/InlineHint";
import { Spinner } from "../components/ui/spinner";
import WorkspaceTab from "../features/workspaces/WorkspaceTab";
import WorkspaceSwitcher, { WorkspaceNavTab } from "../features/workspaces/WorkspaceSwitcher";

type EditTabKey = WorkspaceNavTab;

const EditPage: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { owner, repo } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { workspaceKey, branch, group, setParam } = useWorkspaceEditorParams();
  const [activeTab, setActiveTab] = useState<EditTabKey>("editor");
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [groups, setGroups] = useState<RepositoryGroup[]>([]);
  const [workspaceState, setWorkspaceState] = useState<WorkspaceStateResponse | null>(null);
  const [workspaceConfig, setWorkspaceConfig] = useState<WorkspaceConfig | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [errorText, setErrorText] = useState("");

  React.useEffect(() => {
    const bootstrap = async () => {
      if (!owner || !repo) return;
      setIsBusy(true);
      setErrorText("");
      try {
        const [{ workspaces: workspaceList }, config, groupsResponse, stateResponse] = await Promise.all([
          localizationClient.getWorkspaces(owner, repo),
          localizationClient.getWorkspace(owner, repo, workspaceKey),
          localizationClient.getRepositoryGroups(owner, repo),
          localizationClient.getWorkspaceState(owner, repo, workspaceKey),
        ]);
        setWorkspaces(workspaceList);
        setWorkspaceConfig(config);
        setGroups(groupsResponse.groups);
        setWorkspaceState(stateResponse);
      } catch (error) {
        setErrorText(parseApiError(error).message);
      } finally {
        setIsBusy(false);
      }
    };
    bootstrap();
  }, [owner, repo, workspaceKey]);

  const refreshWorkspaceState = React.useCallback(async () => {
    if (!owner || !repo) return;
    try {
      const data = await localizationClient.getWorkspaceState(owner, repo, workspaceKey);
      setWorkspaceState(data);
    } catch (error) {
      setErrorText(parseApiError(error).message);
    }
  }, [owner, repo, workspaceKey]);

  const handleWorkspaceUpdated = useCallback((workspace: WorkspaceConfig) => {
    setWorkspaceConfig(workspace);
  }, []);

  const handleBranchChange = useCallback(
    (nextBranch: string) => setParam("branch", nextBranch),
    [setParam]
  );

  const handleGroupChange = useCallback(
    (nextGroup: string) => setParam("group", nextGroup),
    [setParam]
  );

  const handleSelectWorkspace = useCallback(
    (nextWorkspaceKey: string) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("workspaceKey", nextWorkspaceKey);
        return next;
      }),
    [setSearchParams]
  );

  const handleCreateWorkspace = useCallback(
    async (payload: { workspaceKey: string; workspaceName: string; description?: string }) => {
      if (!owner || !repo) return;
      try {
        await localizationClient.createWorkspace(owner, repo, payload);
        const response = await localizationClient.getWorkspaces(owner, repo);
        setWorkspaces(response.workspaces);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("workspaceKey", payload.workspaceKey);
          return next;
        });
      } catch (error) {
        const parsed = parseApiError(error);
        setErrorText(parsed.message);
        toast({
          variant: "destructive",
          title: parsed.code,
          description: parsed.message,
          action: parsed.requestId ? (
            <ToastAction
              altText="Copy request ID"
              onClick={() => navigator.clipboard?.writeText(parsed.requestId || "")}
            >
              {parsed.requestId}
            </ToastAction>
          ) : undefined,
        });
      }
    },
    [owner, repo, setSearchParams, toast]
  );

  const handleRenameWorkspace = useCallback(
    async (payload: { workspaceName: string; description?: string }) => {
      if (!owner || !repo) return;
      try {
        await localizationClient.patchWorkspace(owner, repo, workspaceKey, payload);
        const response = await localizationClient.getWorkspaces(owner, repo);
        setWorkspaces(response.workspaces);
      } catch (error) {
        const parsed = parseApiError(error);
        setErrorText(parsed.message);
      }
    },
    [owner, repo, workspaceKey]
  );

  const handleDeleteWorkspace = useCallback(async () => {
    if (!owner || !repo) return;
    try {
      await localizationClient.deleteWorkspace(owner, repo, workspaceKey);
      const response = await localizationClient.getWorkspaces(owner, repo);
      setWorkspaces(response.workspaces);
      const fallback = response.workspaces[0]?.workspaceKey || "default";
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("workspaceKey", fallback);
        return next;
      });
    } catch (error) {
      const parsed = parseApiError(error);
      setErrorText(parsed.message);
    }
  }, [owner, repo, workspaceKey, setSearchParams]);

  const tabContent = useMemo(() => {
    if (!owner || !repo) return <span>Invalid repository route</span>;

    if (activeTab === "editor") {
      return (
        <EditorTab
          owner={owner}
          repo={repo}
          workspaceKey={workspaceKey}
          branch={branch}
          group={group}
          onWorkspaceUpdated={handleWorkspaceUpdated}
          onBranchChange={handleBranchChange}
          onGroupChange={handleGroupChange}
        />
      );
    }
    if (activeTab === "bindings") {
      return (
        <BindingsTab
          owner={owner}
          repo={repo}
          workspaceKey={workspaceKey}
          onWorkspaceUpdated={handleWorkspaceUpdated}
        />
      );
    }
    if (activeTab === "workspace") {
      return (
        <WorkspaceTab
          workspaces={workspaces}
          workspaceKey={workspaceKey}
          state={workspaceState}
          isBusy={isBusy}
          onCreateWorkspace={handleCreateWorkspace}
          onRenameWorkspace={handleRenameWorkspace}
          onDeleteWorkspace={handleDeleteWorkspace}
          onRefreshState={refreshWorkspaceState}
          groupsSlot={
            <GroupsManager
              groups={groups}
              isBusy={isBusy}
              onCreateGroup={async (payload) => {
                try {
                  await localizationClient.createRepositoryGroup(owner, repo, payload);
                  const response = await localizationClient.getRepositoryGroups(owner, repo);
                  setGroups(response.groups);
                } catch (error) {
                  const parsed = parseApiError(error);
                  setErrorText(parsed.message);
                }
              }}
              onRenameGroup={async (groupKey, payload) => {
                try {
                  await localizationClient.patchRepositoryGroup(owner, repo, groupKey, payload);
                  const response = await localizationClient.getRepositoryGroups(owner, repo);
                  setGroups(response.groups);
                } catch (error) {
                  const parsed = parseApiError(error);
                  setErrorText(parsed.message);
                }
              }}
              onDeleteGroup={async (groupKey) => {
                try {
                  await localizationClient.deleteRepositoryGroup(owner, repo, groupKey);
                  const response = await localizationClient.getRepositoryGroups(owner, repo);
                  setGroups(response.groups);
                } catch (error) {
                  const parsed = parseApiError(error);
                  setErrorText(parsed.message);
                }
              }}
            />
          }
        />
      );
    }
    return <ActivityTab owner={owner} repo={repo} workspaceKey={workspaceKey} />;
  }, [
    activeTab,
    owner,
    repo,
    workspaceKey,
    branch,
    group,
    workspaces,
    groups,
    workspaceState,
    isBusy,
    handleCreateWorkspace,
    handleRenameWorkspace,
    handleDeleteWorkspace,
    refreshWorkspaceState,
    handleWorkspaceUpdated,
    handleBranchChange,
    handleGroupChange,
  ]);

  return (
    <>
      <Header user={user} repositoryName={repo} owner={owner} />
      <Wrap>
        <Card>
          {owner && repo ? (
            <WorkspaceSwitcher
              workspaces={workspaces}
              workspaceKey={workspaceKey}
              activeTab={activeTab}
              isBusy={isBusy}
              onSelectWorkspace={handleSelectWorkspace}
              onSelectTab={setActiveTab}
            />
          ) : null}
          {workspaceConfig?.workspaceName ? (
            <p className="text-sm text-muted-foreground mb-3">
              Active workspace: {workspaceConfig.workspaceName} ({workspaceKey})
              <span className="ml-1 inline-flex align-middle">
                <InlineHint text="Workspace key is shared across all tabs and controls which configuration and data are loaded." />
              </span>
            </p>
          ) : null}
          {isBusy ? (
            <p className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" />
              Loading workspace context...
            </p>
          ) : null}
          {errorText ? <p className="text-sm text-red-500 mb-2">{errorText}</p> : null}
          {tabContent}
          <Toaster />
        </Card>
      </Wrap>
    </>
  );
};

export default EditPage;
